import type { HistoryItem, ServerMessage } from './protocol';
import type { ClientErrorCode } from './errorCopy';

export type ConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'ready'
  | 'idle'
  | 'closed';

export type ToolStatus =
  | 'generating'
  | 'running'
  | 'ok'
  | 'error'
  | 'cancelled';

export type ChatItem =
  | { kind: 'user'; id: string; text: string; accepted?: boolean }
  | {
      kind: 'assistant';
      id: string;
      text: string;
      thinking: string;
      done: boolean;
    }
  | (Omit<HistoryTool, 'kind' | 'isError'> & {
      kind: 'tool';
      id: string;
      status: ToolStatus;
      partial: string;
      argsSummary?: string;
      chars?: number;
    })
  | ({ kind: 'notice'; id: string; isError: boolean } & NoticeContent);

export type NoticeContent =
  | { source: 'client'; text: string }
  | { source: 'client_error'; code: ClientErrorCode }
  | { source: 'server'; code: string; params?: unknown }
  | { source: 'model' };

type HistoryTool = Extract<HistoryItem, { kind: 'tool' }>;

export interface PiChatState {
  connectionState: ConnectionState;
  isStreaming: boolean;
  items: ChatItem[];
  closedReason: string | null;
  closedParams: unknown;
  currentAssistantId: string | null;
  turnActive: boolean;
  seq: number;
  resumed: boolean;
  returnedDrafts: ReturnedDraft[];
  conversationId: string | null;
  reconnecting: boolean;
}

export interface ReturnedDraft {
  id: string;
  text: string;
}

const INFORMATIONAL_ERROR_CODES = new Set<string>([
  'not_ready',
  'busy',
  'restore_busy',
  'closed',
  'cancelled',
  'files_skipped',
  'restore_note_failed',
]);

const START_REFUSED_ERROR_CODES = new Set<string>([
  'invalid_context',
  'bad_request',
]);

export const SESSION_ENDING_ERROR_CODES = [
  'conflict',
  'not_found',
  'forbidden',
  'auth',
] as const;
export type SessionEndingErrorCode =
  (typeof SESSION_ENDING_ERROR_CODES)[number];

const isSessionEndingErrorCode = (
  code: string
): code is SessionEndingErrorCode =>
  (SESSION_ENDING_ERROR_CODES as readonly string[]).includes(code);

export const CREDENTIALS_FAILED_REASON = 'credentials_failed';

const EXPLAINED_CLOSE_REASONS = [
  ...SESSION_ENDING_ERROR_CODES,
  CREDENTIALS_FAILED_REASON,
] as const;
export type ExplainedCloseReason = (typeof EXPLAINED_CLOSE_REASONS)[number];

export const selectExplainedCloseReason = (
  state: Pick<PiChatState, 'connectionState' | 'closedReason'>
): ExplainedCloseReason | null =>
  state.connectionState === 'closed' &&
  (EXPLAINED_CLOSE_REASONS as readonly (string | null)[]).includes(
    state.closedReason
  )
    ? (state.closedReason as ExplainedCloseReason)
    : null;

export const selectIsBusy = (
  state: Pick<PiChatState, 'isStreaming' | 'turnActive'>
) => state.isStreaming || state.turnActive;

export const selectCanSend = (state: Pick<PiChatState, 'connectionState'>) =>
  state.connectionState === 'ready' || state.connectionState === 'idle';

export const isRecoverableClose = (
  state: Pick<PiChatState, 'connectionState' | 'closedReason'>,
  reason: string
) =>
  state.connectionState !== 'closed' &&
  state.closedReason === null &&
  reason !== 'client' &&
  !(EXPLAINED_CLOSE_REASONS as readonly string[]).includes(reason);

export const selectCanReconnect = (
  state: Pick<PiChatState, 'connectionState' | 'closedReason'>
) => {
  const reason = selectExplainedCloseReason(state);
  return reason !== 'not_found' && reason !== 'forbidden';
};

export type PiChatAction =
  | { type: 'connecting' }
  | { type: 'reconnecting' }
  | { type: 'idle' }
  | { type: 'server'; message: ServerMessage }
  | { type: 'userSent'; text: string; clientMessageId: string }
  | { type: 'socketClosed'; reason: string }
  | { type: 'socketError' }
  | ({ type: 'clientNotice'; isError?: boolean } & (
      | { text: string }
      | { code: ClientErrorCode }
    ));

export const initialState: PiChatState = {
  connectionState: 'disconnected',
  isStreaming: false,
  items: [],
  closedReason: null,
  closedParams: null,
  currentAssistantId: null,
  turnActive: false,
  seq: 0,
  resumed: false,
  returnedDrafts: [],
  conversationId: null,
  reconnecting: false,
};

export const ARGS_SUMMARY_MAX_LENGTH = 120;
const ARGS_SUMMARY_KEYS = ['path', 'command', 'pattern', 'query', 'url'];

export const summarizeToolArgs = (args: unknown): string => {
  if (typeof args !== 'object' || args === null || Array.isArray(args)) {
    return '';
  }
  const record = args as Record<string, unknown>;
  const key =
    ARGS_SUMMARY_KEYS.find((name) => typeof record[name] === 'string') ??
    Object.keys(record).find((name) => typeof record[name] === 'string');
  if (!key) return '';
  const firstLine = (record[key] as string).trim().split('\n', 1)[0].trim();
  return firstLine.length > ARGS_SUMMARY_MAX_LENGTH
    ? `${firstLine.slice(0, ARGS_SUMMARY_MAX_LENGTH)}…`
    : firstLine;
};

const isUnfinishedTool = (item: ChatItem) =>
  item.kind === 'tool' &&
  (item.status === 'generating' || item.status === 'running');

const cancelUnfinishedTools = (items: ChatItem[]): ChatItem[] =>
  items.some(isUnfinishedTool)
    ? items.map((item) =>
        item.kind === 'tool' && isUnfinishedTool(item)
          ? { ...item, status: 'cancelled' as const }
          : item
      )
    : items;

const nextId = (state: PiChatState, prefix: string) =>
  `${prefix}-${state.seq + 1}`;

const appendNotice = (
  state: PiChatState,
  content: NoticeContent,
  isError: boolean
): PiChatState => ({
  ...state,
  seq: state.seq + 1,
  items: [
    ...state.items,
    { kind: 'notice', id: nextId(state, 'notice'), isError, ...content },
  ],
});

const updateItem = (
  items: ChatItem[],
  id: string,
  update: (item: ChatItem) => ChatItem
) => items.map((item) => (item.id === id ? update(item) : item));

const returnRejectedMessage = (
  state: PiChatState,
  clientMessageId: string
): PiChatState => {
  const rejected = state.items.find(
    (item) => item.kind === 'user' && item.id === clientMessageId
  );
  if (!rejected || rejected.kind !== 'user' || rejected.accepted) return state;
  return {
    ...state,
    items: state.items.filter((item) => item.id !== clientMessageId),
    returnedDrafts: [
      ...state.returnedDrafts,
      { id: clientMessageId, text: rejected.text },
    ],
  };
};

const acceptMessage = (
  state: PiChatState,
  clientMessageId: string
): PiChatState =>
  state.items.some(
    (item) =>
      item.kind === 'user' && item.id === clientMessageId && !item.accepted
  )
    ? {
        ...state,
        turnActive: true,
        items: updateItem(state.items, clientMessageId, (item) =>
          item.kind === 'user' ? { ...item, accepted: true } : item
        ),
      }
    : state;

const endSession = (state: PiChatState, reason: string): PiChatState => ({
  ...state,
  connectionState: 'closed',
  reconnecting: false,
  isStreaming: false,
  turnActive: false,
  currentAssistantId: null,
  closedReason: state.closedReason ?? reason,
  items: state.items.map((item) => {
    if (item.kind === 'assistant' && !item.done) {
      return { ...item, done: true };
    }
    if (
      item.kind === 'tool' &&
      (item.status === 'running' || item.status === 'generating')
    ) {
      return { ...item, status: 'error' as const };
    }
    return item;
  }),
});

const historyToItems = (history: HistoryItem[], seq: number): ChatItem[] =>
  history.map((entry, index): ChatItem => {
    const id = `history-${seq + index + 1}`;
    switch (entry.kind) {
      case 'user':
        return { kind: 'user', id, text: entry.text };
      case 'assistant':
        return {
          kind: 'assistant',
          id,
          text: entry.text,
          thinking: '',
          done: true,
        };
      case 'tool':
        return {
          kind: 'tool',
          id,
          toolName: entry.toolName,
          status: entry.isError ? 'error' : 'ok',
          partial: '',
          summary: entry.summary,
          ...(entry.toolCallId ? { toolCallId: entry.toolCallId } : {}),
          ...(entry.capture ? { capture: entry.capture } : {}),
        };
    }
  });

const reduceServerMessage = (
  state: PiChatState,
  message: ServerMessage
): PiChatState => {
  switch (message.type) {
    case 'session.ready': {
      const { history, conversationId } = message;
      const ready = {
        connectionState: 'ready' as const,
        reconnecting: false,
        closedReason: null,
        closedParams: null,
        resumed: message.resumed === true,
        turnActive: message.turnActive,
        conversationId,
      };
      const sameConversation =
        state.reconnecting && conversationId === state.conversationId;
      if (sameConversation && (message.turnActive || !selectIsBusy(state))) {
        return { ...state, ...ready };
      }
      const base = sameConversation
        ? {
            ...state,
            isStreaming: false,
            turnActive: false,
            currentAssistantId: null,
            items: [],
          }
        : state.reconnecting
          ? initialState
          : state;
      return {
        ...base,
        ...ready,
        ...(history.length
          ? {
              seq: base.seq + history.length,
              items: [...historyToItems(history, base.seq), ...base.items],
            }
          : {}),
      };
    }
    case 'assistant.delta': {
      const field = message.kind === 'thinking' ? 'thinking' : 'text';
      if (state.currentAssistantId) {
        return {
          ...state,
          isStreaming: true,
          turnActive: true,
          items: updateItem(state.items, state.currentAssistantId, (item) =>
            item.kind === 'assistant'
              ? { ...item, [field]: item[field] + message.delta }
              : item
          ),
        };
      }
      const id = nextId(state, 'assistant');
      return {
        ...state,
        isStreaming: true,
        turnActive: true,
        seq: state.seq + 1,
        currentAssistantId: id,
        items: [
          ...state.items,
          {
            kind: 'assistant',
            id,
            text: field === 'text' ? message.delta : '',
            thinking: field === 'thinking' ? message.delta : '',
            done: false,
          },
        ],
      };
    }
    case 'assistant.message': {
      let next: PiChatState = state;
      if (state.currentAssistantId) {
        const currentId = state.currentAssistantId;
        const items = state.items
          .map((item) =>
            item.id === currentId && item.kind === 'assistant'
              ? { ...item, text: message.text || item.text, done: true }
              : item
          )
          .filter(
            (item) =>
              !(
                item.id === currentId &&
                item.kind === 'assistant' &&
                !item.text &&
                !item.thinking
              )
          );
        next = { ...state, items, currentAssistantId: null };
      } else if (message.text) {
        next = {
          ...state,
          seq: state.seq + 1,
          items: [
            ...state.items,
            {
              kind: 'assistant',
              id: nextId(state, 'assistant'),
              text: message.text,
              thinking: '',
              done: true,
            },
          ],
        };
      }
      return message.stopReason === 'error'
        ? appendNotice(next, { source: 'model' }, true)
        : next;
    }
    case 'tool.generating': {
      const existing = state.items.find(
        (item) => item.kind === 'tool' && item.id === message.toolCallId
      );
      if (existing) {
        return {
          ...state,
          isStreaming: true,
          turnActive: true,
          items: updateItem(state.items, message.toolCallId, (item) =>
            item.kind === 'tool' && item.status === 'generating'
              ? {
                  ...item,
                  chars: message.chars,
                  argsSummary: message.argsSummary ?? item.argsSummary,
                }
              : item
          ),
        };
      }
      return {
        ...state,
        isStreaming: true,
        turnActive: true,
        items: [
          ...state.items,
          {
            kind: 'tool',
            id: message.toolCallId,
            toolName: message.toolName,
            status: 'generating',
            partial: '',
            summary: '',
            argsSummary: message.argsSummary,
            chars: message.chars,
          },
        ],
      };
    }
    case 'tool.start': {
      const argsSummary = summarizeToolArgs(message.args);
      const generating = state.items.some(
        (item) => item.kind === 'tool' && item.id === message.toolCallId
      );
      if (generating) {
        return {
          ...state,
          isStreaming: true,
          turnActive: true,
          items: updateItem(state.items, message.toolCallId, (item) =>
            item.kind === 'tool'
              ? {
                  ...item,
                  toolName: message.toolName,
                  status: 'running',
                  argsSummary: argsSummary || item.argsSummary,
                }
              : item
          ),
        };
      }
      return {
        ...state,
        isStreaming: true,
        turnActive: true,
        items: [
          ...state.items,
          {
            kind: 'tool',
            id: message.toolCallId,
            toolName: message.toolName,
            status: 'running',
            partial: '',
            summary: '',
            argsSummary,
          },
        ],
      };
    }
    case 'tool.update':
      return {
        ...state,
        items: updateItem(state.items, message.toolCallId, (item) =>
          item.kind === 'tool' ? { ...item, partial: message.partial } : item
        ),
      };
    case 'tool.end':
      return {
        ...state,
        items: updateItem(state.items, message.toolCallId, (item) =>
          item.kind === 'tool'
            ? {
                ...item,
                status: message.isError ? 'error' : 'ok',
                summary: message.summary,
                ...(message.capture ? { capture: message.capture } : {}),
              }
            : item
        ),
      };
    case 'turn.settled':
      return {
        ...state,
        isStreaming: false,
        turnActive: false,
        currentAssistantId: null,
        items: cancelUnfinishedTools(state.items),
      };
    case 'error': {
      const { code, params } = message;
      const refusesStart =
        START_REFUSED_ERROR_CODES.has(code) &&
        state.connectionState !== 'ready';
      const busyWithOwnTurn =
        message.code === 'busy' &&
        message.params.reason === 'conversation_locked' &&
        message.clientMessageId !== undefined &&
        state.turnActive;
      let next =
        isSessionEndingErrorCode(code) || refusesStart
          ? state
          : appendNotice(
              state,
              busyWithOwnTurn
                ? { source: 'client_error', code: 'busy_own_turn' }
                : {
                    source: 'server',
                    code,
                    ...(params !== undefined ? { params } : {}),
                  },
              !INFORMATIONAL_ERROR_CODES.has(code)
            );
      if (message.clientMessageId) {
        next = returnRejectedMessage(
          state.turnActive
            ? { ...next, isStreaming: true }
            : {
                ...next,
                isStreaming: false,
                items: cancelUnfinishedTools(next.items),
              },
          message.clientMessageId
        );
      }
      if (isSessionEndingErrorCode(code)) {
        return { ...next, closedReason: code, closedParams: params ?? null };
      }
      if (refusesStart) {
        return endSession(
          next.closedReason ? next : { ...next, closedParams: params ?? null },
          code
        );
      }
      return next;
    }
    case 'message.accepted':
      return acceptMessage(state, message.clientMessageId);
    case 'session.closed':
      return endSession(state, message.reason);
    default:
      return state;
  }
};

export const piChatReducer = (
  state: PiChatState,
  action: PiChatAction
): PiChatState => {
  switch (action.type) {
    case 'connecting':
      return { ...initialState, connectionState: 'connecting' };
    case 'reconnecting':
      return { ...state, connectionState: 'connecting', reconnecting: true };
    case 'idle':
      return { ...state, connectionState: 'idle', reconnecting: false };
    case 'server':
      return reduceServerMessage(state, action.message);
    case 'userSent':
      return {
        ...state,
        isStreaming: true,
        seq: state.seq + 1,
        items: [
          ...state.items,
          { kind: 'user', id: action.clientMessageId, text: action.text },
        ],
      };
    case 'socketClosed':
      return state.connectionState === 'disconnected'
        ? state
        : endSession(state, action.reason);
    case 'socketError':
      return appendNotice(
        state,
        { source: 'client_error', code: 'socket_error' },
        true
      );
    case 'clientNotice':
      return appendNotice(
        state,
        'code' in action
          ? { source: 'client_error', code: action.code }
          : { source: 'client', text: action.text },
        action.isError ?? true
      );
    default:
      return state;
  }
};
